import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBoqs } from "../../api/boq";
import { useCustomerOrders } from "../../api/customerOrders";
import { formatDate } from "../../api/dates";
import { useInvoices } from "../../api/invoices";
import { formatMoney } from "../../api/money";
import { useOrders } from "../../api/purchaseOrders";
import { Card, Segmented, StatusChip } from "../../components/ui";
import { draftLink, draftStanding } from "./DraftRows";
import { jobLink, type StepKey } from "./pipeline";
import { boqLabel, boqTone, customerOrderLabel, customerOrderTone, orderLabel, orderTone } from "./tones";

type Ledger = "draft" | "po" | "boq" | "customer" | "invoice";

/** Every document raised, across all jobs -- the tool's "Purchase orders
 * raised" panel, widened to the ledgers the commercial chain produces: the
 * office's draft BOQs, then the four documents that follow the quotation.
 *
 * A summary, not a workspace: each row names its document and opens it on the
 * job it belongs to, at that document's step (Fix #7 -- the order's link used
 * to drop the step, and the City's orders and our invoices skipped the job).
 * A row is reached and opened from the keyboard like a link (Fix #17), and a
 * status reads as its label, never as its key (Fix #4). */
export default function DocumentsPanel() {
  const [ledger, setLedger] = useState<Ledger>("po");
  const orders = useOrders("");
  const boqs = useBoqs("");
  const customer = useCustomerOrders();
  const invoices = useInvoices();
  // The office's drafts are never the City's bill, so they have a tab of their own.
  const drafts = boqs.data?.rows.filter((row) => row.is_draft);
  const bills = boqs.data?.rows.filter((row) => !row.is_draft);

  const n = (rows: unknown[] | undefined) => (rows === undefined ? "" : ` ${rows.length}`);

  return (
    <Card
      title="Documents raised"
      sub="Across every job"
      className="cm-ledgers"
      actions={
        <Segmented<Ledger>
          label="Which documents"
          value={ledger}
          onChange={setLedger}
          options={[
            { value: "draft", label: `Draft BOQs${n(drafts)}` },
            { value: "po", label: `Supplier POs${n(orders.data?.rows)}` },
            { value: "boq", label: `Customer BOQs${n(bills)}` },
            { value: "customer", label: `Customer POs${n(customer.data?.rows)}` },
            { value: "invoice", label: `Invoices${n(invoices.data?.rows)}` },
          ]}
        />
      }
      flush
    >
      {ledger === "draft" ? (
        <LedgerTable
          label="Draft bills of quantities"
          empty="No draft BOQ started yet."
          error={boqs.error?.message ?? null}
          head={["Works order", "Site", "Standing", "Lines", "Excl VAT"]}
          amounts={[3, 4]}
          dates={[]}
          rows={(drafts ?? []).map((row) => {
            const standing = draftStanding(row);
            return {
              key: row.id,
              name: row.name,
              job: row.job_id,
              step: "draft" as StepKey,
              fallback: draftLink(row),
              cells: [
                row.work_order_no ?? row.name,
                row.site?.name ?? "—",
                <StatusChip key="s" tone={standing.tone}>
                  {standing.label}
                </StatusChip>,
                String(row.line_count),
                formatMoney(row.total_excl_vat),
              ],
            };
          })}
        />
      ) : null}

      {ledger === "po" ? (
        <LedgerTable
          label="Supplier purchase orders"
          empty="No order raised yet."
          error={orders.error?.message ?? null}
          head={["Number", "Supplier", "Site", "Status", "Dated", "Excl VAT", "Supplier invoiced"]}
          amounts={[5, 6]}
          dates={[4]}
          rows={(orders.data?.rows ?? []).map((row) => ({
            key: row.id,
            name: row.number,
            job: row.quotation_id,
            step: "po" as StepKey,
            fallback: `/purchase-orders/${row.id}`,
            cells: [
              row.number,
              row.supplier?.name ?? "—",
              row.site?.name ?? "—",
              <StatusChip key="s" tone={orderTone(row.status)}>
                {orderLabel(row.status)}
              </StatusChip>,
              formatDate(row.po_date),
              formatMoney(row.total_excl_vat),
              row.supplier_invoice ? formatMoney(row.supplier_invoice.amount) : "—",
            ],
          }))}
        />
      ) : null}

      {ledger === "boq" ? (
        <LedgerTable
          label="Customer bills of quantities"
          empty="No bill raised yet."
          error={boqs.error?.message ?? null}
          head={["Bill", "Site", "Status", "Dated", "Lines", "Excl VAT", "Invoice"]}
          amounts={[4, 5]}
          dates={[3]}
          rows={(bills ?? []).map((row) => ({
            key: row.id,
            name: row.name,
            job: row.quotation_id,
            step: "boq" as StepKey,
            fallback: `/boq/${row.id}`,
            cells: [
              row.name,
              row.site?.name ?? "—",
              <StatusChip key="s" tone={boqTone(row.status)}>
                {boqLabel(row.status)}
              </StatusChip>,
              formatDate(row.boq_date),
              String(row.line_count),
              formatMoney(row.total_excl_vat),
              row.invoice?.number ?? "—",
            ],
          }))}
        />
      ) : null}

      {ledger === "customer" ? (
        <LedgerTable
          label="The City's purchase orders"
          empty="The City has sent no order yet."
          error={customer.error?.message ?? null}
          head={["Order", "Site", "Status", "Dated", "Their order", "Our bill", "Invoiced"]}
          amounts={[4, 5, 6]}
          dates={[3]}
          rows={(customer.data?.rows ?? []).map((row) => ({
            key: row.id,
            name: row.orderNumber,
            job: row.boq?.quotation_id ?? null,
            step: "customer" as StepKey,
            fallback: `/customer-orders/${row.id}`,
            cells: [
              row.orderNumber,
              row.site?.name ?? "—",
              <StatusChip key="s" tone={customerOrderTone(row.status)}>
                {customerOrderLabel(row.status)}
              </StatusChip>,
              formatDate(row.orderDate),
              formatMoney(row.values.poTotal),
              formatMoney(row.values.boqTotal),
              formatMoney(row.values.invoiceTotal),
            ],
          }))}
        />
      ) : null}

      {ledger === "invoice" ? (
        <LedgerTable
          label="Tax invoices to the City"
          empty="Nothing invoiced yet."
          error={invoices.error?.message ?? null}
          head={["Invoice", "Bill", "Site", "Dated", "Penalty", "Excl VAT", "Incl VAT"]}
          amounts={[4, 5, 6]}
          dates={[3]}
          rows={(invoices.data?.rows ?? []).map((row) => ({
            key: row.id,
            name: row.number,
            job: row.boq?.quotation_id ?? null,
            step: "invoice" as StepKey,
            fallback: `/invoices/${row.id}`,
            cells: [
              row.number,
              row.boq?.name ?? "—",
              row.site?.name ?? "—",
              formatDate(row.invoice_date),
              formatMoney(row.penalty),
              formatMoney(row.excl_vat),
              formatMoney(row.total_incl_vat),
            ],
          }))}
        />
      ) : null}
    </Card>
  );
}

interface Row {
  key: number;
  /** The document's own number, for the row's accessible name. */
  name: string;
  /** The quotation this document belongs to, where it is worked on. */
  job: number | null;
  step: StepKey;
  /** Where the document opens on its own when it has no job (raised by hand
   * before the chain was joined up). */
  fallback: string;
  cells: ReactNode[];
}

function LedgerTable({
  label,
  head,
  rows,
  empty,
  error,
  amounts,
  dates,
}: {
  label: string;
  head: string[];
  rows: Row[];
  empty: string;
  error: string | null;
  /** Which columns carry money or counts, and so read down their right edge. */
  amounts: number[];
  /** Which columns carry a date. Dates and amounts stay on one line. */
  dates: number[];
}) {
  const navigate = useNavigate();
  if (error !== null) {
    return <p className="notice bad">{error}</p>;
  }
  if (rows.length === 0) {
    return <p className="empty-state">{empty}</p>;
  }
  const target = (row: Row) => (row.job !== null ? jobLink(row.job, row.step) : row.fallback);
  const cellClass = (index: number) =>
    amounts.includes(index) ? "num" : dates.includes(index) ? "date" : index === 0 ? "mono" : undefined;
  function onKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: Row) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigate(target(row));
    }
  }
  return (
    <div className="cm-ledger-scroll">
      <table className="data-table" aria-label={label}>
        <thead>
          <tr>
            {head.map((title, index) => (
              <th key={title} className={amounts.includes(index) ? "num" : undefined}>
                {title}
              </th>
            ))}
            <th aria-label="Open" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className="clickable"
              tabIndex={0}
              aria-label={`${row.name}, open ${row.job !== null ? "its job" : "the document"}`}
              onClick={() => navigate(target(row))}
              onKeyDown={(event) => onKeyDown(event, row)}
              data-testid={`ledger-row-${row.key}`}
            >
              {row.cells.map((cell, index) => (
                <td key={index} className={cellClass(index)}>
                  {cell}
                </td>
              ))}
              <td className="num">
                <Link to={target(row)} className="go-link" tabIndex={-1} onClick={(event) => event.stopPropagation()}>
                  {row.job !== null ? "Open the job →" : "Open →"}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
